/* eslint-disable @typescript-eslint/naming-convention */
import { captureException } from '@sentry/browser';
import axios, { CancelTokenSource } from 'axios';
import { DropzoneFile } from 'dropzone';
import React, { ComponentType } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchGet, fetchPost } from '../../../store/fetch';
import { openApiFetchPost } from '../../../store/openApiFetch';
import { DropzoneComponent } from '../../utils/dropzone/DropzoneComponent';

interface IAssetDropzoneReduxProps {
    userToken: string;
}

async function wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

interface IAssetDropzoneProps extends ITranslateProps, IAssetDropzoneReduxProps {
    assetType?: string | undefined;
    objectId?: string | undefined;
    surveyId?: string | undefined;
    elementId?: string | undefined;
    unitId?: string | undefined;
    manifestationId?: string | undefined;
    failureModeId?: string | undefined;
    inspectionStandardId?: string | undefined;
    utProjectId?: string | undefined;
    equipmentId?: string | undefined;
    batchId?: string | undefined;
    acceptedFiles?: string | undefined;
    onError?: (errorCode: string) => void;
    onComplete: (file: IDropzoneFile) => void;
    onSending?: () => void;
    onQueueComplete?: () => void;
    onAddedFile?: (file: IDropzoneFile) => void;
    maxFiles?: number | undefined;
    maxFileSizeMb?: number | undefined;
    maxFileSizePerType?: { extension: string; sizeMb: number }[];
    assetMetadata?: { [key: string]: any };
    autoProcessQueue: boolean | undefined;
    parallelUploads?: number | undefined;
    // Forces the backend to treat the upload as a 360° equirectangular video,
    // bypassing the ffprobe-based detection. Useful when source metadata was
    // stripped or never present (e.g. transcoded DJI .osv exports).
    forcePanorama?: boolean | undefined;
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        userToken: authentication.token,
    };
}

interface IDropzoneFile extends DropzoneFile {
    name: string;
    s3Id: string;
    s3UploadId?: string;
    completedParts: { partNumber: number; eTag: string }[];
    useChunkedUpload: boolean;
    s3UploadUrl: string;
    type: string;
    status: 'uploading' | 'success';
    cancelTokenSource?: CancelTokenSource;
}

class AssetDropzoneComponent extends React.Component<IAssetDropzoneProps, Record<string, unknown>> {
    private dropzone: any;

    constructor(props: IAssetDropzoneProps) {
        super(props);

        this.state = {};
    }

    public override UNSAFE_componentWillReceiveProps(nextProps: IAssetDropzoneProps) {
        if (this.props.autoProcessQueue !== nextProps.autoProcessQueue) {
            if (nextProps.autoProcessQueue) {
                this.dropzone.processQueue();
            }
        }
    }

    public override render() {
        return (
            <div className="tray-bin pl10 mb10" style={{ minHeight: 270 }}>
                <DropzoneComponent
                    config={{
                        postUrl: '/api/asset/invalid',
                    }}
                    djsConfig={{
                        maxFiles: this.props.maxFiles,
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        accept: async (file: IDropzoneFile, done: (error?: Error) => void) => {
                            if (!file.upload) {
                                throw new Error('File upload is not defined');
                            }
                            let maxFileSize = this.props.maxFileSizeMb ?? 0;
                            if (this.props.maxFileSizePerType) {
                                maxFileSize =
                                    this.props.maxFileSizePerType.find((item) => item.extension === file.name.split('.').pop())?.sizeMb ?? 0;
                            }
                            try {
                                if (maxFileSize && file.upload.total > maxFileSize * 1024 * 1024) {
                                    console.log(`Max file size: ${maxFileSize}  Total file size: ${file.upload.total}`);
                                    throw new Error('FILE_TOO_BIG');
                                } else if (file.upload.total > 5 * 1024 * 1024) {
                                    const response = await fetchGet(`/api/assets/multipartUpload`, { contentType: file.type });
                                    file.s3Id = response.json.id;
                                    file.s3UploadId = response.json.uploadId;
                                    file.completedParts = [];
                                    file.useChunkedUpload = true;
                                    if (response.json.uploadId.startsWith('/localStorage/upload')) {
                                        file.s3UploadUrl = response.json.uploadId;
                                    }
                                    done();
                                } else {
                                    // cache buster is used to prevent caching of the upload url in Safari
                                    const response = await fetchGet(`/api/assets/uploadurl`, { contentType: file.type, cacheBuster: uuid() });
                                    file.s3Id = response.json.id;
                                    file.s3UploadUrl = response.json.url;
                                    file.useChunkedUpload = false;
                                    done();
                                }
                            } catch (error) {
                                if ('statusCode' in error && error.statusCode === 401) {
                                    throw error;
                                }
                                console.error(error);
                                done(error);
                                captureException(error);
                            }
                        },
                        method: 'PUT',
                        autoProcessQueue: this.props.autoProcessQueue,
                        acceptedFiles: this.props.acceptedFiles,
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        maxFilesize: 2_000_000,
                        addRemoveLinks: true,
                        dictDefaultMessage: renderToStaticMarkup(
                            <span style={{ borderColor: 'blue' }}>
                                <i className="fa fa-cloud-upload "></i>
                                <span className="main-text ">
                                    <b>{this.props.t('survey.dropFiles')}</b> {this.props.t('survey.toUpload')}
                                </span>{' '}
                                <br />
                                <div className="sub-text ">
                                    ({this.props.t('survey.orClick')})
                                    {this.props.acceptedFiles && (
                                        <div className="text-muted mt10">
                                            {this.props.t('survey.dropzoneAcceptedFiles', this.props.acceptedFiles)}
                                        </div>
                                    )}
                                </div>
                            </span>,
                        ),
                        dictFileTooBig: 'FILE_TOO_BIGx',
                        dictResponseError: this.props.t('survey.serverNotConfigured'),
                        headers: { 'zxy-token': this.props.userToken },
                        previewTemplate: renderToStaticMarkup(
                            <div className="dz-preview dz-file-preview">
                                <div className="dz-details">
                                    <div className="dz-filename">
                                        <span data-dz-name="true"></span>
                                    </div>
                                    <img data-dz-thumbnail="true" />
                                </div>
                                <div className="dz-progress">
                                    <span className="dz-upload" data-dz-uploadprogress="true"></span>
                                </div>
                                <div className="dz-success-mark">
                                    <span>✔</span>
                                </div>
                                <div className="dz-error-mark">
                                    <span>✘</span>
                                </div>
                                <div className="dz-error-message">
                                    <span data-dz-errormessage="true"></span>
                                </div>
                            </div>,
                        ),
                    }}
                    eventHandlers={{
                        init: this.onDropzoneInit.bind(this),
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        complete: this.onDropzoneComplete.bind(this),
                        uploadprogress: this.onDropzoneUploadProgress.bind(this),

                        canceled: (file: IDropzoneFile) => {
                            file.cancelTokenSource?.cancel();
                        },

                        error: this.onDropzoneError.bind(this),
                        queuecomplete: this.props.onQueueComplete as () => void,
                        addedfile: this.props.onAddedFile as (file: IDropzoneFile) => void,
                    }}
                />
            </div>
        );
    }

    public cancelUploads() {
        // workaround for bug https://github.com/enyo/dropzone/issues/1083

        for (const el of document.getElementsByClassName('dz-preview') as any) {
            el.outerHTML = '';
        }
        for (const file of this.dropzone.files) {
            file.previewElement = undefined;
            this.dropzone.removeFile(file);
        }
        for (const el of document.getElementsByClassName('dz-preview') as any) {
            el.outerHTML = '';
        }
        if (this.props.onQueueComplete) {
            this.props.onQueueComplete();
        }
    }

    private onDropzoneInit(dropzone: any) {
        this.dropzone = dropzone;

        // override the uploadFiles function to send via axios instead of xhr
        dropzone.uploadFiles = async (files: IDropzoneFile[]) => {
            for (let j = 0; j < files.length; j++) {
                const file = files[j]!;
                await sendFile(file);
            }
        };

        async function sendFile(file: IDropzoneFile) {
            const { CancelToken } = axios;
            const source = CancelToken.source();
            file.cancelTokenSource = source;

            if (file.s3UploadUrl?.startsWith('/localStorage/upload')) {
                const formData = new FormData();
                formData.append('file', file);
                await axios.put(file.s3UploadUrl, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                file.status = 'success';
                dropzone.emit('complete', file);
                return;
            }

            if (!file.useChunkedUpload) {
                try {
                    await axios.put(file.s3UploadUrl, file, {
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.floor((progressEvent.loaded * 100) / (progressEvent.total ?? file.size));
                            dropzone.emit('uploadprogress', file, percentCompleted, progressEvent.loaded);
                        },
                        headers: { 'Content-Type': file.useChunkedUpload ? '' : file.type },
                        cancelToken: source.token,
                        timeout: 1000 * 60 * 60 * 2,
                    });
                    file.status = 'success';
                    dropzone.emit('complete', file);
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        dropzone.emit('error', file, error.message);
                    }
                }
            } else {
                const fileChunkSize = 1024 * 1024 * 10; // 10MB
                const fileSize = file.upload?.total ?? file.size;
                const numberOfChunks = Math.floor(fileSize / fileChunkSize) + 1;
                const maxRetries = 20;
                let currentTry = 0;

                try {
                    for (let i = 0; i < numberOfChunks; i++) {
                        try {
                            currentTry++;
                            const start = (i + 1 - 1) * fileChunkSize;
                            const end = (i + 1) * fileChunkSize;
                            const blob = i + 1 < numberOfChunks ? (file as any).slice(start, end) : (file as any).slice(start);
                            const uploadUrlResponse = await fetchGet(
                                `/api/assets/uploadPartUrl?contentType=${file.type}&key=${file.s3Id}&partNumber=${i + 1}&uploadId=${file.s3UploadId}`,
                            );
                            const uploadUrl = uploadUrlResponse.json.presignedUrl;
                            const uploadResponse = await axios.put(uploadUrl, blob, {
                                onUploadProgress: (progressEvent) => {
                                    const chunkPercentCompleted = (progressEvent.loaded * 100) / (progressEvent.total ?? file.size) / numberOfChunks;
                                    const percentCompleted = (i * 100) / numberOfChunks + chunkPercentCompleted;
                                    dropzone.emit('uploadprogress', file, percentCompleted, fileSize);
                                },
                                headers: { 'Content-Type': file.useChunkedUpload ? '' : file.type },
                                cancelToken: source.token,
                                timeout: 1000 * 60 * 60,
                            });
                            file.completedParts.push({ eTag: uploadResponse.headers.etag!, partNumber: i + 1 });
                            currentTry = 0;
                        } catch (error) {
                            if (currentTry > maxRetries) {
                                throw error;
                            } else {
                                await wait(15000);
                                i--;
                            }
                        }
                    }
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        dropzone.emit('error', file, error.message);
                    }
                    return;
                }
                await fetchPost('/api/assets/completeMultipartUpload', undefined, {
                    key: file.s3Id,
                    uploadId: file.s3UploadId,
                    completedParts: file.completedParts,
                    contentType: file.type,
                });
                file.status = 'success';
                dropzone.emit('complete', file);
            }
        }
    }

    private onDropzoneError(file: any, errormessage: any, xhr?: any) {
        if (errormessage === 'Upload canceled.') {
            return;
        }

        console.log('error', file, errormessage, xhr);

        let detailedMessage = undefined as string | undefined;
        if (xhr) {
            detailedMessage = 'Server: ';
        } else {
            detailedMessage = 'Dropzone: ';
        }
        if (typeof errormessage === 'string') {
            detailedMessage += errormessage;
        } else if (errormessage.message) {
            detailedMessage += errormessage.message;
        } else {
            detailedMessage += JSON.stringify(errormessage);
        }

        if (this.props.onError) {
            this.props.onError(detailedMessage);
        }
    }

    private async onDropzoneComplete(file: any) {
        if (file.status !== 'success') {
            this.props.onComplete(file);
            this.dropzone.processQueue();
            return;
        }

        const data = {
            file: {
                name: file.name,
                size: file.size,
                type: file.type,
                s3Id: file.s3Id,
                width: file.width,
                height: file.height,
            },
            assetMetadata: this.props.assetMetadata,
        };

        let url: string;
        // import for equipment is handled separately
        if (this.props.equipmentId) {
            url = `/api/assets/equipment?equipmentId=${this.props.equipmentId}`;
            await openApiFetchPost('/api/equipmentAsset', { equipmentId: this.props.equipmentId, type: this.props.assetType }, { data });
        } else {
            if (this.props.batchId) {
                url = `/api/assets?batchId=${this.props.batchId}`;
            } else {
                url = `/api/assets?objectId=${this.props.objectId}`;
                url += this.props.surveyId ? `&surveyId=${this.props.surveyId}` : '';
                url += this.props.elementId ? `&elementId=${this.props.elementId}` : '';
                url += this.props.unitId ? `&unitId=${this.props.unitId}` : '';
                url += this.props.manifestationId ? `&manifestationId=${this.props.manifestationId}` : '';
                url += this.props.failureModeId ? `&failureModeId=${this.props.failureModeId}` : '';
                url += this.props.batchId ? `&batchId=${this.props.batchId}` : '';
                url += this.props.assetType ? `&type=${this.props.assetType}` : '';
                url += this.props.utProjectId ? `&utProjectId=${this.props.utProjectId}` : '';
                url += this.props.forcePanorama ? `&forcePanorama=true` : '';
            }
            try {
                await fetchPost(url, undefined, data);
            } catch (error) {
                console.log('Error while completing file', error);
            }
        }
        this.dropzone.removeFile(file);

        this.props.onComplete(file);
        this.dropzone.processQueue();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private onDropzoneUploadProgress(file: any, progress: number) {
        if (progress === 100) {
            if (file.previewElement.querySelector('.dz-remove')) {
                file.previewElement.querySelector('.dz-remove').outerHTML = this.props.t('survey.processing');
            }
        }
    }
}

const AssetDropzone = translate()(
    connect(mapStateToProps, undefined, undefined, {})(AssetDropzoneComponent) as ComponentType<
        Omit<IAssetDropzoneProps, keyof ReturnType<typeof mapStateToProps>>
    >,
);

export { AssetDropzone };
